/**
 * fmwvtool.config.schema.ts - Configgr Schema for FMwvTool
 * 
 * Defines the complete configuration schema for FMwvTool using Configgr.
 * All settings should be managed through this schema.
 * 
 * Integrates with:
 * - Skinnr: For available skins
 * - Templatr: For available templates
 * - Profylr: For profile-based settings
 * 
 * Note: Skin and template enums are populated dynamically at runtime
 * by the settings component to avoid module-load-time dependencies.
 */

export const fmwvtoolConfigSchema = {
  // Appearance settings
  appearance: {
    skin: {
      type: 'string',
      // Enum populated dynamically - see SettingsPanelConfiggr
      enum: ['compact', 'fmwvtool-default', 'grid-light', 'grid-dark', 'opcentral-dash'],
      default: 'fmwvtool-default',
      description: 'Active skin/theme',
      category: 'appearance',
      label: 'Skin',
    },
    template: {
      type: 'string',
      // Enum populated dynamically from Templatr - see SettingsPanelConfiggr
      enum: ['none', 'compact-dashboard', 'high-contrast', 'field-ops'],
      default: 'none',
      description: 'Shared appearance template (team/group overrides)',
      category: 'appearance',
      label: 'Appearance Template',
      help: 'Templates provide shared CSS overrides for teams/contexts. Create templates in Templatr.',
    },
    mode: {
      type: 'string',
      enum: ['light', 'dark', 'auto'],
      default: 'auto',
      description: 'Theme mode',
      category: 'appearance',
    },
    fontSize: {
      type: 'string',
      enum: ['tiny', 'small', 'medium', 'large', 'xlarge'],
      default: 'medium',
      description: 'Base font size',
      category: 'appearance',
      label: 'Font Size',
    },
    spacing: {
      type: 'string',
      enum: ['minimal', 'compact', 'comfortable', 'spacious', 'relaxed'],
      default: 'comfortable',
      description: 'Space between elements (margins, gaps)',
      category: 'appearance',
      label: 'Spacing',
    },
    padding: {
      type: 'string',
      enum: ['minimal', 'compact', 'comfortable', 'spacious', 'relaxed'],
      default: 'comfortable',
      description: 'Space inside elements (padding)',
      category: 'appearance',
      label: 'Padding',
    },
  },
  
  // Navigation settings
  navigation: {
    hideNav: {
      type: 'boolean',
      default: false,
      description: 'Hide top navigation bar',
      category: 'navigation',
    },
    hideSidebar: {
      type: 'boolean',
      default: false,
      description: 'Hide sidebar navigation',
      category: 'navigation',
    },
    showCustomizeButton: {
      type: 'boolean',
      default: false,
      description: 'Show customize button when nav is hidden',
      category: 'navigation',
    },
    defaultTool: {
      type: 'string',
      default: 'home',
      description: 'Default tool to open on startup',
      category: 'navigation',
    },
    menuMode: {
      type: 'string',
      enum: ['list', 'dropdown', 'menu', 'modal'],
      default: 'list',
      description: 'Navigation menu display mode',
      category: 'navigation',
    },
    searchable: {
      type: 'boolean',
      default: true,
      description: 'Enable menu search',
      category: 'navigation',
    },
    grouped: {
      type: 'boolean',
      default: true,
      description: 'Group menu items by category',
      category: 'navigation',
    },
  },
  
  // Data submission settings
  dataSubmission: {
    saveMode: {
      type: 'string',
      enum: ['auto', 'batch', 'manual'],
      default: 'batch',
      description: 'Data save mode',
      category: 'data',
    },
    submitMethod: {
      type: 'string',
      enum: ['script', 'odata', 'dapi', 'webhook'],
      default: 'script',
      description: 'Data submission method',
      category: 'data',
    },
    autoSaveDelay: {
      type: 'number',
      default: 1000,
      min: 100,
      max: 10000,
      description: 'Auto-save delay in milliseconds',
      category: 'data',
    },
    
    // FileMaker script settings
    fileMakerScript: {
      defaultScriptName: {
        type: 'string',
        default: 'jsRunScript',
        description: 'Default FileMaker script name',
        category: 'data',
      },
      timeout: {
        type: 'number',
        default: 30000,
        min: 1000,
        max: 300000,
        description: 'Script timeout in milliseconds',
        category: 'data',
      },
    },
    
    // oData settings
    odata: {
      defaultEndpoint: {
        type: 'string',
        default: '',
        description: 'Default oData endpoint URL',
        category: 'data',
      },
      authType: {
        type: 'string',
        enum: ['none', 'basic', 'bearer', 'oauth'],
        default: 'basic',
        description: 'Authentication type',
        category: 'data',
      },
      timeout: {
        type: 'number',
        default: 10000,
        min: 1000,
        max: 60000,
        description: 'Request timeout in milliseconds',
        category: 'data',
      },
    },
    
    // Data API settings
    dapi: {
      defaultEndpoint: {
        type: 'string',
        default: '',
        description: 'FileMaker Data API endpoint',
        category: 'data',
      },
      authType: {
        type: 'string',
        enum: ['token', 'oauth'],
        default: 'token',
        description: 'Authentication type',
        category: 'data',
      },
      timeout: {
        type: 'number',
        default: 10000,
        min: 1000,
        max: 60000,
        description: 'Request timeout in milliseconds',
        category: 'data',
      },
    },
  },
  
  // Plugin-specific settings
  plugins: {
    outliner: {
      defaultView: {
        type: 'string',
        enum: ['single', 'multi'],
        default: 'single',
        description: 'Default field view',
        category: 'plugins',
      },
      autoSave: {
        type: 'boolean',
        default: false,
        description: 'Enable auto-save',
        category: 'plugins',
      },
      expandOnLoad: {
        type: 'boolean',
        default: false,
        description: 'Expand all nodes on load',
        category: 'plugins',
      },
      showToolbar: {
        type: 'boolean',
        default: true,
        description: 'Show toolbar',
        category: 'plugins',
      },
      showFieldViewToggle: {
        type: 'boolean',
        default: true,
        description: 'Show field view toggle button',
        category: 'plugins',
      },
      showImportExport: {
        type: 'boolean',
        default: true,
        description: 'Show FileMaker import/export buttons',
        category: 'plugins',
      },
      fontSize: {
        type: 'string',
        default: '14px',
        description: 'Default font size',
        category: 'plugins',
      },
    },
    
    pdfViewer: {
      defaultZoom: {
        type: 'number',
        default: 1.0,
        min: 0.25,
        max: 4.0,
        description: 'Default zoom level',
        category: 'plugins',
      },
      showToolbar: {
        type: 'boolean',
        default: true,
        description: 'Show PDF toolbar',
        category: 'plugins',
      },
      enableAnnotations: {
        type: 'boolean',
        default: false,
        description: 'Enable PDF annotations',
        category: 'plugins',
      },
    },
    
    picker: {
      mode: {
        type: 'string',
        enum: ['single', 'multi', 'tree'],
        default: 'single',
        description: 'Selection mode',
        category: 'plugins',
      },
      searchable: {
        type: 'boolean',
        default: true,
        description: 'Enable search',
        category: 'plugins',
      },
      showIcons: {
        type: 'boolean',
        default: true,
        description: 'Show item icons',
        category: 'plugins',
      },
    },
    
    fieldMatcher: {
      autoMatch: {
        type: 'boolean',
        default: true,
        description: 'Auto-match similar field names',
        category: 'plugins',
      },
      fuzzyMatching: {
        type: 'boolean',
        default: true,
        description: 'Enable fuzzy matching',
        category: 'plugins',
      },
      showPreview: {
        type: 'boolean',
        default: true,
        description: 'Show data preview',
        category: 'plugins',
      },
    },
    
    tagManager: {
      mode: {
        type: 'string',
        enum: ['compact', 'full', 'inline', 'modal', 'cloud'],
        default: 'compact',
        description: 'Display mode',
        category: 'plugins',
      },
      allowCreate: {
        type: 'boolean',
        default: true,
        description: 'Allow creating new tags',
        category: 'plugins',
      },
      showColors: {
        type: 'boolean',
        default: true,
        description: 'Show tag colors',
        category: 'plugins',
      },
    },
  },
  
  // Development settings
  development: {
    samplesEnabled: {
      type: 'boolean',
      default: true,
      description: 'Show sample data',
      category: 'development',
    },
    debugMode: {
      type: 'boolean',
      default: false,
      description: 'Enable debug mode',
      category: 'development',
    },
    showDebugInfo: {
      type: 'boolean',
      default: false,
      description: 'Show debug information in UI',
      category: 'development',
    },
    verboseLogging: {
      type: 'boolean',
      default: false,
      description: 'Enable verbose console logging',
      category: 'development',
    },
    logLevel: {
      type: 'string',
      enum: ['debug', 'info', 'warn', 'error'],
      default: 'info',
      description: 'Logging level',
      category: 'development',
    },
  },
  
  // Accessibility settings
  accessibility: {
    highContrast: {
      type: 'boolean',
      default: false,
      description: 'Enable high contrast mode',
      category: 'accessibility',
    },
    reduceMotion: {
      type: 'boolean',
      default: false,
      description: 'Reduce animations',
      category: 'accessibility',
    },
    keyboardNavigation: {
      type: 'boolean',
      default: true,
      description: 'Enable keyboard navigation',
      category: 'accessibility',
    },
    screenReaderMode: {
      type: 'boolean',
      default: false,
      description: 'Optimize for screen readers',
      category: 'accessibility',
    },
  },
  
  // Performance settings
  performance: {
    lazyLoading: {
      type: 'boolean',
      default: true,
      description: 'Enable lazy loading',
      category: 'performance',
    },
    cacheEnabled: {
      type: 'boolean',
      default: true,
      description: 'Enable caching',
      category: 'performance',
    },
    maxCacheSize: {
      type: 'number',
      default: 50,
      min: 10,
      max: 500,
      description: 'Maximum cache size in MB',
      category: 'performance',
    },
    virtualScrolling: {
      type: 'boolean',
      default: true,
      description: 'Enable virtual scrolling for large lists',
      category: 'performance',
    },
  },
};

/**
 * Get default configuration values
 */
export function getDefaultConfig(): Record<string, any> {
  const defaults: Record<string, any> = {};
  
  function extractDefaults(schema: any, prefix = '') {
    for (const [key, value] of Object.entries(schema)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      
      if (value && typeof value === 'object') {
        if ('default' in value) {
          defaults[fullKey] = value.default;
        } else {
          extractDefaults(value, fullKey);
        }
      }
    }
  }
  
  extractDefaults(fmwvtoolConfigSchema);
  return defaults;
}

/**
 * Validate a configuration value against schema
 */
export function validateConfig(key: string, value: any): { valid: boolean; error?: string } {
  const parts = key.split('.');
  let schema: any = fmwvtoolConfigSchema;
  
  // Navigate to the schema definition
  for (const part of parts) {
    if (!schema[part]) {
      return { valid: false, error: `Unknown configuration key: ${key}` };
    }
    schema = schema[part];
  }
  
  // If we're at a nested object, it's valid
  if (!schema.type) {
    return { valid: true };
  }
  
  // Type validation
  if (schema.type === 'boolean' && typeof value !== 'boolean') {
    return { valid: false, error: `Expected boolean, got ${typeof value}` };
  }
  
  if (schema.type === 'number' && typeof value !== 'number') {
    return { valid: false, error: `Expected number, got ${typeof value}` };
  }
  
  if (schema.type === 'string' && typeof value !== 'string') {
    return { valid: false, error: `Expected string, got ${typeof value}` };
  }
  
  // Enum validation
  if (schema.enum && !schema.enum.includes(value)) {
    return { valid: false, error: `Value must be one of: ${schema.enum.join(', ')}` };
  }
  
  // Range validation for numbers
  if (schema.type === 'number') {
    if (schema.min !== undefined && value < schema.min) {
      return { valid: false, error: `Value must be >= ${schema.min}` };
    }
    if (schema.max !== undefined && value > schema.max) {
      return { valid: false, error: `Value must be <= ${schema.max}` };
    }
  }
  
  return { valid: true };
}

/**
 * Get configuration schema for a specific category
 */
export function getSchemaByCategory(category: string): Record<string, any> {
  const result: Record<string, any> = {};
  
  function findByCategory(schema: any, prefix = '') {
    for (const [key, value] of Object.entries(schema)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      
      if (value && typeof value === 'object') {
        if ('category' in value && value.category === category) {
          result[fullKey] = value;
        } else if (!('type' in value)) {
          findByCategory(value, fullKey);
        }
      }
    }
  }
  
  findByCategory(fmwvtoolConfigSchema);
  return result;
}

export default fmwvtoolConfigSchema;
